import { computed } from "vue";
import useCreateComponentConfig, { EStates } from "../../../composables/useCreateComponentConfig.js";
import { ESelectorItemElements, ESelectorItemVariants } from "../types.js";
import focus from "../../constants/focus.js";
import { SIZES } from "@/ui/ts/types/Sizes";
const DEFAULT_SIZE = SIZES.MEDIUM;
export default function useSelectorItemConfig(props) {
  const root = useRootConfig(props);
  const headerContainer = useHeaderContainerConfig(props);
  const label = useLabelConfig(props);
  const radio = useRadioConfig(props);
  const radioIndicator = useRadioIndicatorConfig();
  const radioIndicatorIcon = useRadioIndicatorIconConfig();
  const paragraph = useParagraphConfig(props);
  return {
    root,
    headerContainer,
    label,
    radio,
    radioIndicator,
    radioIndicatorIcon,
    paragraph
  };
}
function useRootConfig(props) {
  const { setBase, addBaseState, addSize, addVariant, setDefault, getClasses } = useCreateComponentConfig();
  const elementName = ESelectorItemElements.ROOT;
  setBase(
    elementName,
    /* tw */
    `
            flex w-full flex-col
            transition-colors duration-200 ease-linear
            cursor-pointer
            border-2 rounded-container-sm border-neutral-subtlest
            aria-checked:border-brand-accent-on-dark
            aria-disabled:border-disabled aria-disabled:cursor-not-allowed
        `
  );
  addBaseState(
    elementName,
    EStates.HOVER,
    /* tw */
    `hover:border-neutral-subtle`
  );
  addBaseState(
    elementName,
    EStates.PRESSED,
    /* tw */
    `active:border-neutral-default`
  );
  addBaseState(
    elementName,
    EStates.FOCUSED,
    /* tw */
    `${focus}`
  );
  addVariant(elementName, ESelectorItemVariants.SIMPLE, {
    /* tw */
    base: `${props.badge ? (
      /* tw */
      "pt-xs px-xs pb-sm"
    ) : (
      /* tw */
      "py-base px-sm"
    )} ${!props.badge && /* tw */
    "justify-center"}`,
    states: {}
  });
  addVariant(elementName, ESelectorItemVariants.CHECK, {
    /* tw */
    base: `p-sm ${props.isGroupHasAnyBadge && /* tw */
    "pb-base"}`,
    states: {}
  });
  addVariant(elementName, ESelectorItemVariants.COMPACT, {
    /* tw */
    base: `p-sm items-center justify-center`,
    states: {}
  });
  addSize(
    elementName,
    DEFAULT_SIZE,
    /* tw */
    ""
  );
  setDefault(elementName, ESelectorItemVariants.SIMPLE, DEFAULT_SIZE);
  const rootClasses = computed(() => {
    return getClasses(elementName, props.variant || ESelectorItemVariants.SIMPLE, DEFAULT_SIZE);
  });
  return rootClasses.value;
}
function useHeaderContainerConfig(props) {
  const { setBase, setDefault, addVariant, getClasses } = useCreateComponentConfig();
  const elementName = ESelectorItemElements.HEADER_CONTAINER;
  setBase(
    elementName,
    /* tw */
    `flex w-full items-center gap-x-1`
  );
  addVariant(elementName, ESelectorItemVariants.SIMPLE, {
    base: (
      /* tw */
      `justify-center ${props.badge ? "mb-1" : ""}`
    ),
    states: {}
  });
  addVariant(elementName, ESelectorItemVariants.CHECK, {
    base: (
      /* tw */
      `justify-between ${props.isGroupHasAnyBadge || !props.badge && props.paragraph ? "mb-2" : ""}`
    ),
    states: {}
  });
  setDefault(elementName, ESelectorItemVariants.SIMPLE, DEFAULT_SIZE);
  const headerContainerClasses = computed(() => {
    return getClasses(elementName, props.variant || ESelectorItemVariants.SIMPLE);
  });
  return headerContainerClasses.value;
}
function useLabelConfig(props) {
  const { setBase, addVariant, setDefault, getClasses } = useCreateComponentConfig();
  const elementName = ESelectorItemElements.LABEL;
  setBase(
    elementName,
    /* tw */
    `
            label-md font-default font-semibold text-neutral-primary line-clamp-3 text-ellipsis break-words
            cursor-pointer word-break-word
            aria-disabled:text-disabled aria-disabled:cursor-not-allowed
        `
  );
  addVariant(elementName, ESelectorItemVariants.SIMPLE, {
    base: (
      /* tw */
      "text-center"
    ),
    states: {}
  });
  addVariant(elementName, ESelectorItemVariants.CHECK, {
    base: (
      /* tw */
      ""
    ),
    states: {}
  });
  addVariant(elementName, ESelectorItemVariants.COMPACT, {
    base: (
      /* tw */
      "text-center"
    ),
    states: {}
  });
  setDefault(elementName, ESelectorItemVariants.SIMPLE, DEFAULT_SIZE);
  const labelClasses = computed(() => {
    return getClasses(elementName, props.variant || ESelectorItemVariants.SIMPLE);
  });
  return labelClasses.value;
}
function useRadioConfig(props) {
  const { setBase, addVariant, setDefault, getClasses } = useCreateComponentConfig();
  const elementName = ESelectorItemElements.RADIO;
  setBase(
    elementName,
    /* tw */
    `
            shrink-0 aspect-square size-base rounded-full
            border border-neutral-subtle aria-checked:border-brand-accent
            bg-neutral-primary aria-checked:bg-brand-accent
            ring-offset-background focus:outline-none
            focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
            transition-colors duration-200 ease-linear
            pointer-events-none
            aria-disabled:bg-disabled aria-disabled:border-disabled
            aria-disabled:aria-checked:border-none aria-disabled:cursor-not-allowed self-start
        `
  );
  addVariant(elementName, ESelectorItemVariants.SIMPLE, {
    base: (
      /* tw */
      "hidden"
    ),
    states: {}
  });
  addVariant(elementName, ESelectorItemVariants.CHECK, {
    base: (
      /* tw */
      ""
    ),
    states: {}
  });
  setDefault(elementName, ESelectorItemVariants.SIMPLE, DEFAULT_SIZE);
  const radioClasses = computed(() => {
    return getClasses(elementName, props.variant || ESelectorItemVariants.SIMPLE);
  });
  return radioClasses.value;
}
function useRadioIndicatorConfig() {
  const { setBase, getClasses } = useCreateComponentConfig();
  const elementName = ESelectorItemElements.RADIO_INDICATOR;
  setBase(
    elementName,
    /* tw */
    `flex size-full items-center justify-center`
  );
  const radioIndicatorClasses = computed(() => {
    return getClasses(elementName);
  });
  return radioIndicatorClasses.value;
}
function useRadioIndicatorIconConfig() {
  const { setBase, getClasses } = useCreateComponentConfig();
  const elementName = ESelectorItemElements.RADIO_INDICATOR_ICON;
  setBase(
    elementName,
    /* tw */
    `
        text-comp-high-contrast-on-light
        aria-disabled:text-disabled
    `
  );
  const radioIndicatorIconClasses = computed(() => {
    return getClasses(elementName);
  });
  return radioIndicatorIconClasses.value;
}
function useParagraphConfig(props) {
  const { setBase, addVariant, setDefault, getClasses } = useCreateComponentConfig();
  const elementName = ESelectorItemElements.RADIO_INDICATOR_ICON;
  setBase(
    elementName,
    /* tw */
    `
            paragraph-sm text-neutral-tertiary font-default font-medium
            line-clamp-2 text-ellipsis word-break-word
            aria-disabled:text-disabled mb-1
        `
  );
  addVariant(elementName, ESelectorItemVariants.SIMPLE, {
    base: (
      /* tw */
      "text-center"
    ),
    states: {}
  });
  setDefault(elementName, ESelectorItemVariants.SIMPLE, DEFAULT_SIZE);
  const paragraphClasses = computed(() => {
    return getClasses(elementName, props.variant || ESelectorItemVariants.SIMPLE);
  });
  return paragraphClasses.value;
}
